import type { TeamResponse } from '../api';

/** Which library the user is looking at. `teamId` is set only for a team. */
export interface Library {
  scope: 'Personal' | 'Team' | 'SharedWithMe';
  teamId?: string;
}

export function libraryLabel(library: Library, teams: TeamResponse[]): string {
  if (library.scope === 'Personal') {
    return 'My drawer';
  }

  if (library.scope === 'SharedWithMe') {
    return 'Shared with me';
  }

  return teams.find((team) => team.id === library.teamId)?.name ?? 'Team';
}

interface LibrarySwitcherProps {
  library: Library;
  teams: TeamResponse[];
  onChange: (library: Library) => void;
  onManageTeams: () => void;
}

export function LibrarySwitcher({
  library,
  teams,
  onChange,
  onManageTeams,
}: LibrarySwitcherProps) {
  const isActive = (candidate: Library) =>
    candidate.scope === library.scope && candidate.teamId === library.teamId;

  function entry(candidate: Library, label: string, badge?: string) {
    return (
      <li key={`${candidate.scope}:${candidate.teamId ?? ''}`}>
        <button
          type="button"
          aria-current={isActive(candidate) ? 'page' : undefined}
          onClick={() => onChange(candidate)}
          className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5
            text-left text-sm transition-colors
            ${
              isActive(candidate)
                ? 'bg-accent-subtle text-text'
                : 'text-text-muted hover:text-text'
            }`}
        >
          <span className="truncate">{label}</span>
          {badge && <span className="shrink-0 text-xs text-text-faint">{badge}</span>}
        </button>
      </li>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <h2 className="px-2 text-xs font-medium tracking-wide text-text-faint uppercase">
        Libraries
      </h2>

      <ul className="flex flex-col gap-0.5">
        {entry({ scope: 'Personal' }, 'My drawer')}
        {entry({ scope: 'SharedWithMe' }, 'Shared with me')}

        {teams.map((team) =>
          entry({ scope: 'Team', teamId: team.id }, team.name, `${team.memberCount}`),
        )}
      </ul>

      <button
        type="button"
        onClick={onManageTeams}
        className="mt-1 rounded-md px-2 py-1 text-left text-xs text-text-faint transition-colors
          hover:text-text"
      >
        {teams.length === 0 ? '+ Create a team' : 'Manage teams'}
      </button>
    </div>
  );
}
